import React, { useState } from 'react';
import { Shield, Lock, AlertCircle } from 'lucide-react';

interface AdminAuthProps {
  onSuccess: () => void;
  onBack: () => void;
}

const AdminAuth: React.FC<AdminAuthProps> = ({ onSuccess, onBack }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Admin API base URL - use proxy in development, current host in production
  const ADMIN_API_BASE =
    process.env.NODE_ENV === 'production'
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:3000'; // Use the React dev server proxy

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Please enter admin password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Use the correct endpoint that exists on the server
      const response = await fetch(
        `${ADMIN_API_BASE}/api/v1/auth/validate-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // Store admin session if needed
        localStorage.setItem('adminSessionId', 'admin-logged-in');
        onSuccess();
      } else {
        setError(data.error || 'Invalid admin password');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center px-4'>
      <div className='max-w-md w-full'>
        {/* Back to main app button */}
        <div className='mb-8'>
          <button
            onClick={onBack}
            className='text-blue-300 hover:text-blue-200 text-sm flex items-center gap-2 transition-colors'
          >
            ← Back to Main App
          </button>
        </div>

        {/* Admin login form */}
        <div className='bg-white rounded-2xl shadow-2xl p-8'>
          <div className='text-center mb-8'>
            <div className='mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4'>
              <Shield className='text-white' size={32} />
            </div>
            <h1 className='text-2xl font-bold text-gray-900 mb-2'>
              Admin Console
            </h1>
            <p className='text-gray-600'>
              Enter admin password to access system controls
            </p>
          </div>

          <form onSubmit={handleSubmit} className='space-y-6'>
            <div>
              <label
                htmlFor='adminPassword'
                className='block text-sm font-medium text-gray-700 mb-2'
              >
                Admin Password
              </label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <Lock className='h-5 w-5 text-gray-400' />
                </div>
                <input
                  type='password'
                  id='adminPassword'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all'
                  placeholder='Enter admin password'
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && (
              <div className='flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm'>
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              type='submit'
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-all ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              } text-white`}
            >
              {isLoading ? (
                <div className='flex items-center justify-center gap-2'>
                  <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin'></div>
                  Authenticating...
                </div>
              ) : (
                'Access Admin Console'
              )}
            </button>
          </form>

          <div className='mt-8 pt-6 border-t border-gray-200'>
            <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
              <div className='flex items-start gap-3'>
                <AlertCircle className='text-amber-600 mt-0.5' size={16} />
                <div className='text-sm'>
                  <p className='text-amber-800 font-medium mb-1'>
                    Admin Access Required
                  </p>
                  <p className='text-amber-700'>
                    This area is restricted to authorized administrators only.
                    All access attempts are logged and monitored.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;
